import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Canonical site URL — use env var so it stays consistent across all pages
export const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://rockhoundauto.com';

// ✅ Static OG Image — deterministic, never changes
export const DEFAULT_OG_IMAGE = 'https://static.readdy.ai/image/b3efdc45e89a2660551e85a9fdfadec5/a59b3ad9d14210b4ce617fff8e7523fb.png';
export const OG_IMAGE_WIDTH = '1200';
export const OG_IMAGE_HEIGHT = '630';

// ─────────────────────────────────────────────────────────────────────────────
// SEO PIXEL LIMIT ENFORCEMENT
// Title:       max 540px  ≈ 55 characters  (Google truncates at ~580px)
// Description: max 975px  ≈ 155 characters (Google truncates at ~1000px)
// These limits are intentionally conservative to stay safely under the hard cap.
// ─────────────────────────────────────────────────────────────────────────────
const TITLE_MAX_CHARS = 55;
const DESC_MAX_CHARS = 155;

/**
 * Enforces the 55-character / ~540px title limit.
 * Truncates at the last word boundary before the limit and appends "…".
 * Logs a warning in development so the issue is caught before deploy.
 */
function enforceTitle(raw: string): string {
  if (raw.length <= TITLE_MAX_CHARS) return raw;

  if (import.meta.env.DEV) {
    console.warn(
      `[SEO] Title too long (${raw.length} chars, max ${TITLE_MAX_CHARS}):\n"${raw}"\nTruncating automatically — fix the source title.`
    );
  }

  // Truncate at last space before the limit so we don't cut mid-word
  const cut = raw.slice(0, TITLE_MAX_CHARS);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 30 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…';
}

/**
 * Enforces the 155-character / ~975px description limit.
 * Truncates at the last sentence or word boundary before the limit.
 * Logs a warning in development so the issue is caught before deploy.
 */
function enforceDescription(raw: string): string {
  if (raw.length <= DESC_MAX_CHARS) return raw;

  if (import.meta.env.DEV) {
    console.warn(
      `[SEO] Description too long (${raw.length} chars, max ${DESC_MAX_CHARS}):\n"${raw}"\nTruncating automatically — fix the source description.`
    );
  }

  const cut = raw.slice(0, DESC_MAX_CHARS);
  // Prefer cutting at a sentence boundary (. ! ?)
  const lastSentence = Math.max(cut.lastIndexOf('. '), cut.lastIndexOf('! '), cut.lastIndexOf('? '));
  if (lastSentence > 80) return cut.slice(0, lastSentence + 1).trimEnd();
  // Fall back to last word boundary
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 80 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…';
}

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogImageWidth?: string | number;
  ogImageHeight?: string | number;
  ogImageAlt?: string;
  ogType?: 'website' | 'article' | 'product';
  twitterCard?: 'summary' | 'summary_large_image';
  robots?: string;
  geoPosition?: string;
  geoRegion?: string;
  geoPlacename?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  noindex?: boolean;
  hreflang?: string;
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * useSEOHead Hook — Canonical SEO Meta Tag Manager
 * ═══════════════════════════════════════════════════════════════════════════
 * ✅ PREFERRED METHOD: Use this hook for all new pages instead of SeoHead component
 * 
 * This hook manages page-specific meta tags using a path-based guard system
 * to prevent race conditions when multiple pages try to write SEO data
 * simultaneously during navigation.
 * 
 * ⚠️ DO NOT use this hook AND SeoHead component on the same page — choose one
 * 
 * ✅ SCHEMA ID CONVENTION:
 *    - Static schemas (index.html): schema-static-[type]
 *    - Runtime schemas (pages): Use injectJsonLd with custom IDs like:
 *      schema-[pagename]-[type] (e.g., schema-financing-webpage)
 *    - This prevents ID collisions between static and dynamic schemas
 * 
 * 📋 USAGE PATTERN:
 *    1. Call useSEOHead() at the top of your page component
 *    2. Use injectJsonLd/removeJsonLd from schemaBuilder.ts for schemas
 *    3. Import business data from @/lib/businessInfo (never hardcode)
 * ═══════════════════════════════════════════════════════════════════════════
 */

// ✅ Guard to prevent duplicate SEO writes — uses stable path-based ID
const SEO_GUARD_ATTR = 'data-seo-managed';

export const useSEOHead = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageWidth = OG_IMAGE_WIDTH,
  ogImageHeight = OG_IMAGE_HEIGHT,
  ogImageAlt,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  robots = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
  geoPosition,
  geoRegion,
  geoPlacename,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  noindex = false,
  hreflang = 'en-US',
}: SEOHeadProps) => {
  // ── Use React Router's location so the path is RELATIVE TO THE BASENAME ──
  // window.location.pathname includes the app's base prefix (e.g. /proj-id/page)
  // useLocation().pathname strips the basename → returns /page (matches route.path)
  // This is critical for the page-health scanner sentinel to match route paths.
  const { pathname: routerPathname } = useLocation();

  useEffect(() => {
    // ✅ Derive canonical from current path if not provided (no trailing slash)
    const resolvedCanonical = canonicalUrl || `${SITE_URL}${routerPathname.replace(/\/$/, '')}`;
    
    const currentPath = routerPathname;

    // ✅ Enforce pixel limits before writing to DOM
    const safeTitle = enforceTitle(title);
    const safeDescription = enforceDescription(description);

    // ✅ Coerce ogImageWidth and ogImageHeight to string
    const imageWidth = String(ogImageWidth);
    const imageHeight = String(ogImageHeight);

    // ✅ Store original values for proper cleanup
    const originalTitle = document.title;
    const originalMeta: Map<HTMLMetaElement, string> = new Map();

    // Set title — use enforced safe version
    document.title = safeTitle;

    // Helper to set or create meta tag with cleanup tracking
    const setMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${property}"]`) as HTMLMetaElement;
      
      if (meta) {
        if (!originalMeta.has(meta)) {
          originalMeta.set(meta, meta.content);
        }
        meta.content = content;
        meta.setAttribute(SEO_GUARD_ATTR, currentPath);
      } else {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, property);
        meta.content = content;
        meta.setAttribute(SEO_GUARD_ATTR, currentPath);
        document.head.appendChild(meta);
        originalMeta.set(meta, '');
      }
    };

    // Set canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    const originalCanonical = canonical?.href || '';
    
    if (canonical) {
      canonical.href = resolvedCanonical;
      canonical.setAttribute(SEO_GUARD_ATTR, currentPath);
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = resolvedCanonical;
      canonical.setAttribute(SEO_GUARD_ATTR, currentPath);
      document.head.appendChild(canonical);
    }

    // Set hreflang link
    let hreflangLink = document.querySelector('link[rel="alternate"][hreflang]') as HTMLLinkElement;
    const originalHreflang = hreflangLink?.href || '';
    
    if (hreflangLink) {
      hreflangLink.href = resolvedCanonical;
      hreflangLink.hreflang = hreflang;
      hreflangLink.setAttribute(SEO_GUARD_ATTR, currentPath);
    } else {
      hreflangLink = document.createElement('link');
      hreflangLink.rel = 'alternate';
      hreflangLink.hreflang = hreflang;
      hreflangLink.href = resolvedCanonical;
      hreflangLink.setAttribute(SEO_GUARD_ATTR, currentPath);
      document.head.appendChild(hreflangLink);
    }

    // ✅ Ensure viewport meta is always present (defensive check)
    if (!document.querySelector('meta[name="viewport"]')) {
      const viewport = document.createElement('meta');
      viewport.name = 'viewport';
      viewport.content = 'width=device-width, initial-scale=1.0, viewport-fit=cover';
      document.head.appendChild(viewport);
    }

    // Basic meta tags
    setMetaTag('description', safeDescription);
    if (keywords) {
      setMetaTag('keywords', keywords);
    }
    
    // ✅ Robots meta — respect noindex flag
    setMetaTag('robots', noindex ? 'noindex, nofollow' : robots);

    // Last-modified
    setMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Open Graph tags
    setMetaTag('og:title', safeTitle, true);
    setMetaTag('og:description', safeDescription, true);
    setMetaTag('og:url', resolvedCanonical, true);
    setMetaTag('og:type', ogType, true);
    setMetaTag('og:image', ogImage, true);
    setMetaTag('og:image:width', imageWidth, true);
    setMetaTag('og:image:height', imageHeight, true);
    setMetaTag('og:image:alt', ogImageAlt || `${safeTitle} — Rockhound Auto`, true);
    setMetaTag('og:site_name', 'Rockhound Auto', true);
    setMetaTag('og:locale', 'en_US', true);

    // Twitter Card tags
    setMetaTag('twitter:card', twitterCard);
    setMetaTag('twitter:title', safeTitle);
    setMetaTag('twitter:description', safeDescription);
    setMetaTag('twitter:image', ogImage);
    setMetaTag('twitter:image:alt', ogImageAlt || `${safeTitle} — Rockhound Auto`);

    // Geo tags (if provided)
    if (geoPosition) {
      setMetaTag('geo.position', geoPosition);
      setMetaTag('ICBM', geoPosition);
    }
    if (geoRegion) {
      setMetaTag('geo.region', geoRegion);
    }
    if (geoPlacename) {
      setMetaTag('geo.placename', geoPlacename);
    }

    // Article-specific tags (if provided)
    if (articlePublishedTime) {
      setMetaTag('article:published_time', articlePublishedTime, true);
    }
    if (articleModifiedTime) {
      setMetaTag('article:modified_time', articleModifiedTime, true);
    }
    if (articleAuthor) {
      setMetaTag('article:author', articleAuthor, true);
    }

    // ✅ SENTINEL: Signal to the page-health scanner that all SEO tags are written.
    // The scanner polls for this attribute instead of guessing with timers.
    // Value = pathname so the scanner can confirm it matches the expected route.
    document.documentElement.setAttribute('data-seo-ready', currentPath);

    // ✅ POSTMESSAGE: If running inside the scanner iframe, push SEO data directly
    // to the parent window. This is event-driven (no polling) so it fires EXACTLY
    // when all tags are written — immune to timing, caching, and StrictMode issues.
    if (window.parent !== window) {
      window.parent.postMessage(
        {
          type: 'rockhound-seo-ready',
          path: currentPath,
          title: safeTitle,
          descriptionLength: safeDescription.length,
          hasCanonical: !!(
            document.querySelector('link[rel="canonical"]')?.getAttribute('href')?.trim()
          ),
          hasH1: !!document.querySelector('h1'),
          isNoindex:
            document
              .querySelector('meta[name="robots"]')
              ?.getAttribute('content')
              ?.toLowerCase()
              .includes('noindex') ?? false,
        },
        '*'
      );
    }

    // ✅ Cleanup on unmount — restore previous values
    return () => {
      // Remove sentinel first so scanner doesn't read a stale state
      if (document.documentElement.getAttribute('data-seo-ready') === currentPath) {
        document.documentElement.removeAttribute('data-seo-ready');
      }

      document.title = originalTitle;
      
      originalMeta.forEach((originalContent, element) => {
        if (element.getAttribute(SEO_GUARD_ATTR) === currentPath) {
          if (originalContent === '') {
            element.remove();
          } else {
            element.content = originalContent;
            element.removeAttribute(SEO_GUARD_ATTR);
          }
        }
      });

      if (canonical && canonical.getAttribute(SEO_GUARD_ATTR) === currentPath) {
        if (originalCanonical) {
          canonical.href = originalCanonical;
          canonical.removeAttribute(SEO_GUARD_ATTR);
        } else {
          canonical.remove();
        }
      }

      if (hreflangLink && hreflangLink.getAttribute(SEO_GUARD_ATTR) === currentPath) {
        if (originalHreflang) {
          hreflangLink.href = originalHreflang;
          hreflangLink.removeAttribute(SEO_GUARD_ATTR);
        } else {
          hreflangLink.remove();
        }
      }
    };
  }, [
    title,
    description,
    keywords,
    canonicalUrl,
    ogImage,
    ogImageWidth,
    ogImageHeight,
    ogImageAlt,
    ogType,
    twitterCard,
    robots,
    geoPosition,
    geoRegion,
    geoPlacename,
    articlePublishedTime,
    articleModifiedTime,
    articleAuthor,
    noindex,
    hreflang,
    routerPathname,
  ]);
};